import { Platform } from 'react-native';
import { AuthManager, type BusinessOwner } from '../utils/authManager';
import type { Appointment } from '../utils/appointmentManager';
import { createCalendarEventsForConnectedCalendars } from './businessCalendarSyncService';

let firestoreUnsubscribe: (() => void) | null = null;

export function stopBusinessCalendarAutoSync(): void {
  if (firestoreUnsubscribe) {
    try {
      firestoreUnsubscribe();
    } catch (e) {
      console.warn('[BookAPT] stopBusinessCalendarAutoSync:', e);
    }
    firestoreUnsubscribe = null;
  }
}

/**
 * Restarts the Firestore listener from saved business owner (toggle + profile).
 */
export async function refreshBusinessCalendarAutoSync(): Promise<void> {
  stopBusinessCalendarAutoSync();
  const owner = await AuthManager.getSavedBusinessOwner();
  if (!owner?.calendarAutoSyncEnabled || !owner.id) return;
  await attachAppointmentListener(owner);
}

async function attachAppointmentListener(owner: BusinessOwner): Promise<void> {
  const businessOwnerId = owner.id;
  if (!businessOwnerId) return;

  const runSync = (apt: Appointment) => {
    createCalendarEventsForConnectedCalendars(apt, owner).catch(err =>
      console.warn('[BookAPT] Auto calendar sync failed:', apt?.id, err)
    );
  };

  if (Platform.OS === 'web') {
    try {
      const { collection, query, where, onSnapshot } = await import('firebase/firestore');
      const { getWebDb, initializeFirebase } = await import('./unifiedAuthService');
      if (!initializeFirebase()) return;
      const db = getWebDb();
      if (!db) return;
      const col = collection(db, 'appointments');
      const q = query(col, where('businessOwnerId', '==', businessOwnerId));
      let firstSnapshot = true;
      firestoreUnsubscribe = onSnapshot(
        q,
        snapshot => {
          if (firstSnapshot) {
            firstSnapshot = false;
            return;
          }
          snapshot.docChanges().forEach(change => {
            if (change.type !== 'added') return;
            const data = change.doc.data() as Record<string, unknown>;
            const apt: Appointment = {
              ...(data as Omit<Appointment, 'id'>),
              id: change.doc.id,
            };
            runSync(apt);
          });
        },
        err => console.warn('[BookAPT] Calendar auto-sync listener (web):', err)
      );
    } catch (e) {
      console.warn('[BookAPT] Calendar auto-sync could not start (web):', e);
    }
    return;
  }

  try {
    const { initializeFirebase } = await import('./unifiedAuthService');
    if (!initializeFirebase()) return;
    const { getApp } = require('@react-native-firebase/app');
    const { getFirestore, collection, query, where, onSnapshot } = require('@react-native-firebase/firestore');
    const db = getFirestore(getApp());
    const col = collection(db, 'appointments');
    const q = query(col, where('businessOwnerId', '==', businessOwnerId));
    let firstSnapshot = true;
    firestoreUnsubscribe = onSnapshot(
      q,
      (snapshot: any) => {
        if (firstSnapshot) {
          firstSnapshot = false;
          return;
        }
        snapshot.docChanges().forEach((change: any) => {
          if (change.type !== 'added') return;
          const apt: Appointment = {
            ...change.doc.data(),
            id: change.doc.id,
          };
          runSync(apt);
        });
      },
      (err: Error) => console.warn('[BookAPT] Calendar auto-sync listener (native):', err)
    );
  } catch (e) {
    console.warn('[BookAPT] Calendar auto-sync could not start (native):', e);
  }
}
